import React, { useState } from 'react';
import { ArrowUpDown } from 'lucide-react';

interface Company {
  id: string;
  name: string;
  ceoName: string;
  founded: {
    day: number;
    month: number;
    year: number;
  };
  productCount: number;
  isPlayer: boolean;
}

interface CompaniesTableProps {
  companies: Company[];
  playerCompanyName?: string;
}

export function CompaniesTable({ companies, playerCompanyName }: CompaniesTableProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Company;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });

  const [filters, setFilters] = useState({
    search: '',
    showPlayerCompany: true,
    showCompetitors: true
  });

  const handleSort = (key: keyof Company) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedCompanies = [...companies]
    .filter(company => {
      const searchMatch = !filters.search || 
        company.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        company.ceoName.toLowerCase().includes(filters.search.toLowerCase());
      
      const filterMatch = 
        (filters.showPlayerCompany && company.isPlayer) ||
        (filters.showCompetitors && !company.isPlayer);

      return searchMatch && filterMatch;
    })
    .sort((a, b) => {
      if (sortConfig.key === 'founded') {
        const dateA = new Date(a.founded.year, a.founded.month - 1, a.founded.day);
        const dateB = new Date(b.founded.year, b.founded.month - 1, b.founded.day);
        return sortConfig.direction === 'asc' 
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];

      if (valueA === valueB) return 0;
      if (valueA === undefined) return 1;
      if (valueB === undefined) return -1;

      const comparison = valueA < valueB ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

  const formatDate = (date: { day: number; month: number; year: number }) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${months[date.month - 1]} ${date.day}, ${date.year}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Search companies..."
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          className="px-3 py-2 border border-theme-border rounded-lg text-theme-text-primary bg-theme-bg-secondary"
        />
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showPlayerCompany}
            onChange={e => setFilters(f => ({ ...f, showPlayerCompany: e.target.checked }))}
          />
          {playerCompanyName || 'Your Company'}
        </label>
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showCompetitors}
            onChange={e => setFilters(f => ({ ...f, showCompetitors: e.target.checked }))}
          />
          Competitors
        </label>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 touch-pan-x">
        <table className="w-full border-collapse min-w-[500px]">
          <thead className="bg-theme-bg-secondary">
            <tr>
              {[
                { key: 'name', label: 'Company' },
                { key: 'founded', label: 'Founded' },
                { key: 'ceoName', label: 'CEO' },
                { key: 'productCount', label: 'Products' }
              ].map(({ key, label }) => (
                <th
                  key={key}
                  onClick={() => handleSort(key as keyof Company)}
                  className="px-4 py-2 text-left text-sm font-medium text-theme-text-muted cursor-pointer hover:bg-theme-bg-tertiary"
                >
                  <div className="flex items-center gap-1">
                    {label}
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedCompanies.map(company => (
              <tr
                key={company.id}
                className={`border-t ${
                  company.isPlayer
                    ? 'bg-theme-accent/10 hover:bg-theme-accent/20'
                    : 'hover:bg-theme-bg-secondary'
                }`}
              >
                <td className="px-4 py-2 font-medium text-theme-text-primary">{company.name}</td>
                <td className="px-4 py-2 text-theme-text-primary">{formatDate(company.founded)}</td>
                <td className="px-4 py-2 text-theme-text-primary">{company.ceoName}</td>
                <td className="px-4 py-2 text-theme-text-primary">{company.productCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}